import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';
import { CompanyLogoVariant, HxLogo, LogoType, ProductType } from '../logo/logo';

export type LogoSize = 'xsmall' | 'small' | 'medium' | 'large';

/**
 * HxFooterComponent - Footer component with Symphony branding
 *
 * Displays the Symphony logo and allows flexible content projection through ng-content. Supports sticky positioning and
 * responsive layout. Extends HxBaseControl for common control properties like cssClass and cssStyle.
 *
 * @example
 *   ```html
 *   <!-- Simple footer with copyright -->
 *   <hx-footer>
 *     <span>All Rights Reserved</span>
 *   </hx-footer>
 *
 *   <!-- Sticky footer with complex content -->
 *   <hx-footer [sticky]="true" logoSize="medium" [logoClickable]="true" (logoClick)="onLogoClick()">
 *     <div class="footer-content">
 *       <span>© 2025 ComplexCare - All Rights Reserved</span>
 *       <nav>
 *         <a href="/privacy">Privacy Policy</a>
 *         <a href="/terms">Terms</a>
 *       </nav>
 *     </div>
 *   </hx-footer>
 *
 *   <!-- Footer with inherited HxBaseControl properties -->
 *   <hx-footer
 *     cssClass="custom-footer"
 *     cssStyle="background: #f0f0f0;">
 *     <span>Custom styled footer</span>
 *   </hx-footer>
 *   ```;
 */
@Component({
  selector: 'hx-footer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, HxLogo],
  templateUrl: './footer.html',
  styleUrls: ['./footer.scss'],
})
export class HxFooter extends HxBaseControl {
  /**
   * Whether the footer should stick to bottom of viewport when content is short
   *
   * @default false
   */
  readonly sticky = input<boolean>(false);

  /**
   * Whether to show the Symphony logo
   *
   * @default true
   */
  readonly showLogo = input<boolean>(true);

  /**
   * Size of the Symphony logo
   *
   * @default 'small'
   */
  readonly logoSize = input<LogoSize>('large');

  /**
   * Whether the logo should be clickable
   *
   * @default false
   */
  readonly logoClickable = input<boolean>(false);

  /**
   * ARIA label for the footer landmark
   *
   * @default 'Page footer'
   */
  readonly ariaLabel = input<string>('Page footer');

  readonly logoType = input<LogoType>('product');
  readonly productType = input<ProductType>('symphony');
  readonly logoVariant = input<CompanyLogoVariant>('full');

  /** Event emitted when the logo is clicked (only when logoClickable is true) */
  readonly logoClick = output<void>();

  /** Computed CSS classes for the footer element */
  readonly cssClasses = computed(() => {
    const classes = ['hx-footer'];

    if (this.sticky()) {
      classes.push('hx-footer--sticky');
    }

    if (this.cssClass()) {
      classes.push(this.cssClass());
    }

    return classes.join(' ');
  });

  /** Handles logo click events */
  onLogoClick(): void {
    if (this.logoClickable()) {
      this.logoClick.emit();
    }
  }
}
